import { useState } from 'react';
import { Download, X, Share, Plus, MoreVertical, ChevronRight } from 'lucide-react';
import { MotionConfig } from 'framer-motion';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import usePwaInstall from '@/hooks/usePwaInstall';
import { toast } from 'sonner';
import { useTriggerFocusReturn } from '@/lib/useTriggerFocusReturn';
import { MODAL_DIALOG_MOTION_CLASSES } from '@/lib/motion';

/*
 * InstallPwaBanner
 *
 * Compact, dismissible banner shown at the top of the Collection page when
 * GroovLens is installable. The behavior adapts to the current device:
 *
 *   - Native prompt captured (Chromium with engagement):
 *       Primary CTA fires the captured beforeinstallprompt directly.
 *   - iOS Safari:
 *       Opens a modal Dialog with clear Share -> Add-to-Home-Screen
 *       instructions, because Apple does not allow programmatic install
 *       and inline copy was being missed by users.
 *   - Chromium without captured prompt yet (Android / desktop):
 *       Opens a modal with menu install instructions.
 *
 * Once the user dismisses the banner, we persist that choice in
 * localStorage so it doesn't re-appear on every load. The always-visible
 * Profile card remains as a backup install entry point.
 */
export default function InstallPwaBanner() {
  const {
    canShowBanner,
    isIos,
    isAndroidChromium,
    isDesktopChromium,
    isFirefoxMobile,
    isSamsungBrowser,
    isOperaMobile,
    isMobile,
    hasNativePrompt,
    promptInstall,
    dismissBanner,
  } = usePwaInstall();
  const [howOpen, setHowOpen] = useState(false);
  // Focus round-trip for the "How to install" howto dialog.
  const { handleCloseAutoFocus } = useTriggerFocusReturn(howOpen);

  if (!canShowBanner) return null;

  const handleInstall = async () => {
    if (hasNativePrompt) {
      const result = await promptInstall();
      if (result?.outcome === 'accepted') {
        toast.success('Installing GroovLens…');
      } else if (result?.outcome === 'dismissed') {
        toast('Maybe next time — you can install later from Profile.');
      }
      return;
    }
    // No native prompt — open the modal with explicit instructions.
    setHowOpen(true);
  };

  const subhead = (() => {
    if (isIos) return 'Add to your home screen for an app-like experience.';
    if (isAndroidChromium) return 'Get a home-screen icon and a full-screen app.';
    if (isDesktopChromium) return 'Faster launches, plus a Dock / Start-menu icon.';
    if (isFirefoxMobile) return 'Add to your home screen via the Firefox menu.';
    if (isSamsungBrowser) return 'Add a home-screen shortcut for fast access.';
    return 'Get a home-screen icon and a full-screen app.';
  })();

  const ctaLabel = hasNativePrompt ? 'Install' : 'Show how';

  return (
    <MotionConfig reducedMotion="user">
      <div
        className="mb-4 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2.5 sm:px-4 sm:py-3 flex items-start gap-3 animate-in fade-in zoom-in-95 duration-100 fill-mode-both motion-reduce:animate-none"
        data-testid="pwa-install-banner"
        role="region"
        aria-label="Install GroovLens as an app"
      >
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))]">
          <Download className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight" data-testid="pwa-banner-title">
            Install GroovLens
          </p>
          <p className="text-xs text-muted-foreground leading-tight mt-0.5">
            {subhead}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            size="sm"
            onClick={handleInstall}
            className="h-8 rounded-lg text-xs px-3"
            data-testid="pwa-banner-install-button"
          >
            {ctaLabel}
          </Button>
          <button
            type="button"
            onClick={dismissBanner}
            className="h-8 w-8 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            aria-label="Dismiss install prompt"
            data-testid="pwa-banner-dismiss-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent
          className={`max-w-md w-[92vw] rounded-2xl bg-card ${MODAL_DIALOG_MOTION_CLASSES}`}
          data-testid="pwa-install-howto-dialog"
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {isIos
                ? 'Install on iPhone'
                : isFirefoxMobile
                ? 'Install on Firefox'
                : isSamsungBrowser
                ? 'Install on Samsung Internet'
                : isOperaMobile
                ? 'Install on Opera'
                : isAndroidChromium
                ? 'Install on Android'
                : isMobile
                ? 'Install on your phone'
                : 'Install on desktop'}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {isIos
                ? "Apple doesn't let apps trigger this for you — but it only takes 2 taps in Safari."
                : isFirefoxMobile
                ? 'Firefox handles install through its menu.'
                : 'Your browser handles install through its menu.'}
            </DialogDescription>
          </DialogHeader>

          {isIos && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-ios-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Tap the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <Share className="w-3.5 h-3.5" /> Share
                  </span>
                  icon at the bottom of Safari.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Scroll down and tap
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <Plus className="w-3.5 h-3.5" /> Add to Home Screen
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">3</span>
                <span className="flex-1">
                  Tap <strong>Add</strong> in the top-right. GroovLens lands on your home screen.
                </span>
              </li>
              <li className="text-xs text-muted-foreground border-t border-border pt-3 mt-1">
                Don&apos;t see the Share button? You may be in a different browser — open <span className="font-mono">groovlens.com</span> in <strong>Safari</strong> directly.
              </li>
            </ol>
          )}

          {!isIos && isFirefoxMobile && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-firefox-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Tap the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <MoreVertical className="w-3.5 h-3.5" /> menu
                  </span>
                  in the top-right of Firefox.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1">
                  Choose <strong>Install</strong> (or <strong>&ldquo;Add to Home screen&rdquo;</strong> on older versions).
                </span>
              </li>
              <li className="text-xs text-muted-foreground border-t border-border pt-3 mt-1">
                Don&apos;t see <strong>Install</strong> in the menu? Firefox needs to be updated, OR
                your version may not support PWA install. For the best experience, open
                <span className="font-mono"> groovlens.com</span> in <strong>Chrome</strong> (Android)
                or <strong>Safari</strong> (iPhone).
              </li>
            </ol>
          )}

          {!isIos && !isFirefoxMobile && isSamsungBrowser && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-samsung-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Tap the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <MoreVertical className="w-3.5 h-3.5" /> menu
                  </span>
                  (three lines / dots) in Samsung Internet.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1">
                  Tap <strong>Add page to</strong> &rarr; <strong>Home screen</strong>.
                </span>
              </li>
            </ol>
          )}

          {!isIos && !isFirefoxMobile && !isSamsungBrowser && isOperaMobile && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-opera-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Tap the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <MoreVertical className="w-3.5 h-3.5" /> menu
                  </span>
                  in Opera.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1">
                  Tap <strong>Add to&hellip;</strong> &rarr; <strong>Home screen</strong>.
                </span>
              </li>
            </ol>
          )}

          {!isIos && !isFirefoxMobile && !isSamsungBrowser && !isOperaMobile && isAndroidChromium && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-android-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Tap the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <MoreVertical className="w-3.5 h-3.5" /> menu
                  </span>
                  in the top-right of Chrome.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1">
                  Tap <strong>Install app</strong> (or &ldquo;Add to Home screen&rdquo;).
                </span>
              </li>
            </ol>
          )}

          {/* Generic mobile fallback — any mobile browser we haven't specifically detected
              (Brave, DuckDuckGo, Edge mobile, in-app webviews, etc.) */}
          {!isIos && !isFirefoxMobile && !isSamsungBrowser && !isOperaMobile && !isAndroidChromium && isMobile && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-generic-mobile-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Open your browser&apos;s
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <MoreVertical className="w-3.5 h-3.5" /> menu
                  </span>
                  (usually 3 dots / lines).
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1">
                  Look for <strong>Install</strong>, <strong>Install app</strong>, or <strong>Add to Home screen</strong>.
                </span>
              </li>
              <li className="text-xs text-muted-foreground border-t border-border pt-3 mt-1">
                If your browser doesn&apos;t offer install, open
                <span className="font-mono"> groovlens.com</span> in
                <strong> Chrome</strong> (Android) or <strong>Safari</strong> (iPhone) — they have the best PWA support.
              </li>
            </ol>
          )}

          {!isIos && !isMobile && isDesktopChromium && (
            <ol className="space-y-3 text-sm" data-testid="pwa-install-desktop-steps">
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">1</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Click the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <Download className="w-3.5 h-3.5" /> install
                  </span>
                  icon at the right edge of the address bar.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center text-xs font-semibold">2</span>
                <span className="flex-1 flex flex-wrap items-center gap-1.5">
                  Or open the
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs">
                    <MoreVertical className="w-3.5 h-3.5" /> menu
                  </span>
                  and choose &ldquo;Install GroovLens&hellip;&rdquo;
                </span>
              </li>
            </ol>
          )}

          <div className="flex justify-end pt-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setHowOpen(false)}
              className="rounded-lg h-8 text-xs"
              data-testid="pwa-install-howto-close"
            >
              Got it <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </MotionConfig>
  );
}
